import React, { createContext, useContext, useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

import Callout from './Callout';
import Tabs from './Tabs';
import Cards from './Cards';

const PreContext = createContext(false);

const LANGUAGE_MAP = {
  curl: 'bash',
  shell: 'bash',
  sh: 'bash',
  zsh: 'bash',
  js: 'javascript',
  ts: 'typescript',
  py: 'python',
  cs: 'csharp',
  yml: 'yaml',
};

function unindentBlock(str) {
  if (!str) return '';
  const lines = str.split('\n');
  let minIndent = Infinity;
  for (const line of lines) {
    if (line.trim().length > 0) {
      const match = line.match(/^(\s*)/);
      if (match && match[1].length < minIndent) {
        minIndent = match[1].length;
      }
    }
  }
  if (minIndent > 0 && minIndent !== Infinity) {
    const regex = new RegExp(`^\\s{1,${minIndent}}`);
    return lines.map((line) => line.replace(regex, '')).join('\n');
  }
  return str;
}

function preprocessMdx(content) {
  if (!content) return '';

  let result = content;

  // 0. Replace environment variable placeholders with runtime process.env values
  const apiBaseUrl = process.env.REACT_APP_PAYD_API_BASE_URL || 'https://business-api.payd.ae';
  const paydUrl = process.env.REACT_APP_PAYD_URL || 'https://payd.ae';
  result = result.replace(/\\?\$\{REACT_APP_PAYD_API_BASE_URL\}/g, apiBaseUrl);
  result = result.replace(/\\?\$\{REACT_APP_PAYD_URL\}/g, paydUrl);

  // 1. Remove Nextra import lines
  result = result.replace(/^import\s+.*?from\s+['"].*?['"];?\s*$/gm, '');

  // 2. Convert items={['a', 'b', ...]} or items={["a", "b", ...]} to items="a|b|..."
  result = result.replace(/items=\{\[(.*?)\]\}/g, (match, group) => {
    const items = group
      .split(',')
      .map((s) => s.trim().replace(/^['"]|['"]$/g, ''))
      .filter(Boolean)
      .join('|');
    return `items="${items}"`;
  });

  // 3. Replace <Tabs.Tab> ... </Tabs.Tab> with <tab> ... </tab> and unindent content
  result = result.replace(/<Tabs\.Tab>([\s\S]*?)<\/Tabs\.Tab>/g, (match, inner) => {
    const unindented = unindentBlock(inner);
    return `\n\n<tab>\n\n${unindented}\n\n</tab>\n\n`;
  });
  result = result.replace(/<Tabs\.Tab>/g, '\n\n<tab>\n\n').replace(/<\/Tabs\.Tab>/g, '\n\n</tab>\n\n');

  // 4. Replace <Tabs ...> and </Tabs> with <tabs ...> and </tabs>
  result = result.replace(/<Tabs\s+/g, '\n\n<tabs ').replace(/<\/Tabs>/g, '\n\n</tabs>\n\n');

  // 5. Replace <Cards.Card ...> and </Cards.Card> with <card ...> and </card> and unindent content
  result = result.replace(/<Cards\.Card\s+([\s\S]*?)>([\s\S]*?)<\/Cards\.Card>/g, (match, attrs, inner) => {
    const unindented = unindentBlock(inner);
    return `\n\n<card ${attrs}>\n\n${unindented}\n\n</card>\n\n`;
  });
  result = result.replace(/<Cards\.Card\s+/g, '\n\n<card ').replace(/<\/Cards\.Card>/g, '\n\n</card>\n\n');

  // 6. Replace <Cards> and </Cards> with <cards> and </cards>
  result = result.replace(/<Cards>/g, '\n\n<cards>\n\n').replace(/<\/Cards>/g, '\n\n</cards>\n\n');

  // 7. Replace <Callout ...> and </Callout> with <callout ...> and </callout> and unindent content
  result = result.replace(/<Callout\s+([\s\S]*?)>([\s\S]*?)<\/Callout>/g, (match, attrs, inner) => {
    const unindented = unindentBlock(inner);
    return `\n\n<callout ${attrs}>\n\n${unindented}\n\n</callout>\n\n`;
  });
  result = result.replace(/<Callout\s+/g, '\n\n<callout ').replace(/<\/Callout>/g, '\n\n</callout>\n\n');

  return result;
}

function PreBlock({ children }) {
  return <PreContext.Provider value={true}>{children}</PreContext.Provider>;
}

function CodeBlock({ node, inline, className, children }) {
  const isPre = useContext(PreContext);
  const isInline = inline !== undefined ? inline : !isPre;
  const match = /language-(\w+)/.exec(className || '');

  if (isInline) {
    return (
      <code className="bg-gray-100 text-cyan-700 px-1.5 py-0.5 rounded text-sm font-mono border border-gray-200">
        {children}
      </code>
    );
  }

  let rawLang = match ? match[1].toLowerCase() : 'text';
  let language = LANGUAGE_MAP[rawLang] || rawLang;
  const codeString = String(children).replace(/\n$/, '');

  return (
    <div className="not-prose my-4 rounded-lg border border-gray-800 bg-[#1e1e1e] shadow-md overflow-hidden">
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={language}
        PreTag="div"
        customStyle={{
          margin: 0,
          padding: '1.25rem',
          backgroundColor: '#1e1e1e',
          fontSize: '0.875rem',
          lineHeight: '1.6',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        }}
      >
        {codeString}
      </SyntaxHighlighter>
    </div>
  );
}

export default function MarkdownViewer({ content, onSelectDoc }) {
  const [fetchedContent, setFetchedContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isUrl = typeof content === 'string' && (content.startsWith('/static/media/') || content.includes('.mdx') || content.startsWith('http'));

  useEffect(() => {
    if (isUrl) {
      let isMounted = true;
      setIsLoading(true);
      fetch(content)
        .then((res) => res.text())
        .then((text) => {
          if (isMounted) {
            setFetchedContent(text);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch markdown from url:', content, err);
          if (isMounted) setIsLoading(false);
        });
      return () => {
        isMounted = false;
      };
    } else {
      setFetchedContent(content || '');
    }
  }, [content, isUrl]);

  const rawText = isUrl ? fetchedContent : (content || '');
  const processedContent = preprocessMdx(rawText);

  const handleLinkClick = (href) => {
    if (!href) return;
    const targetId = href.replace(/^\/api\//, '').replace(/^\//, '');
    if (onSelectDoc && targetId) {
      onSelectDoc(targetId);
    }
  };

  const components = {
    callout: ({ type, children }) => <Callout type={type}>{children}</Callout>,
    tabs: ({ items, children }) => <Tabs items={items}>{children}</Tabs>,
    tab: ({ children }) => <Tabs.Tab>{children}</Tabs.Tab>,
    cards: ({ children }) => <Cards>{children}</Cards>,
    card: ({ title, href, children }) => (
      <Cards.Card title={title} href={href} onClick={() => handleLinkClick(href)}>
        {children}
      </Cards.Card>
    ),
    a: ({ href, children, ...props }) => {
      if (href && href.startsWith('/')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              handleLinkClick(href);
            }}
            className="text-cyan-600 hover:text-cyan-800 underline font-medium cursor-pointer"
            {...props}
          >
            {children}
          </a>
        );
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-600 hover:text-cyan-800 underline font-medium"
          {...props}
        >
          {children}
        </a>
      );
    },
    pre: PreBlock,
    code: CodeBlock,
  };

  if (isLoading) {
    return (
      <div className="py-12 flex flex-col items-center justify-center gap-3 text-gray-400">
        <svg className="animate-spin h-6 w-6 text-cyan-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <span className="text-xs font-medium">Loading documentation...</span>
      </div>
    );
  }

  return (
    <div className="prose max-w-none prose-headings:text-gray-900 prose-h1:text-2xl prose-h1:font-bold prose-h2:text-xl prose-h2:font-semibold prose-h3:text-lg prose-h3:font-medium prose-p:text-gray-700 prose-p:leading-relaxed prose-li:text-gray-700 prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-gray-200 prose-th:p-2 prose-th:bg-gray-50 prose-td:border prose-td:border-gray-200 prose-td:p-2">
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={components}>
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}
